/* tslint:disable:object-literal-sort-keys */
import React, { Dispatch, FunctionComponent, useReducer } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Switch from 'react-switch';
import toastr from '../../../toastr';
import Model from '../../model/ShopKeeper.model';
import Label from '../Label';

interface IProps {
  container: Object;
  hide: Function;
  show: boolean;
  model: Model;
}

type VarianceType = 'Cash Sale' | 'Sales Tax' | 'Core Charges' | 'Fees' | 'No History Sale';
type VariancesState = { [k in VarianceType]: boolean };

const VariancePanelModal: FunctionComponent<IProps> = ({ container, hide, model, show }) => {
  const [variances, setVariances] = useReducer(
    (prevState: VariancesState, newState: VariancesState) => ({ ...prevState, ...newState }),

    // Initial State Defaults
    // Note fleet billing credit card selection does not use this reducer
    {
      'Cash Sale': (model.activeCustomerID && model.customerParams.customerCashOnly) || model.forceCashSale,
      'Sales Tax':
        (model.activeCustomerID && model.customerParams.taxable && !model.suppressSalesTax) || model.forceSalesTax,
      'Core Charges': !model.suppressCoreCharges,
      Fees: !model.suppressFees,
      'No History Sale': model.noHistorySale,
    },

    // Some initial state tweaks depending on the client
    (initialState: VariancesState) => {
      // Only certain customers use this variance, specifically, California-based dealers
      if (!model.sysParams.feeSuppression) {
        // @ts-ignore: The operand of a 'delete' operator must be optional (TS 4+)
        delete initialState['Fees'];
      }

      // Some sites disable core charge suppression
      if (!model.sysParams.coreChargeSuppression) {
        // @ts-ignore: The operand of a 'delete' operator must be optional (TS 4+)
        delete initialState['Core Charges'];
      }

      return initialState;
    }
  ) as [VariancesState, Dispatch<VariancesState>];

  const handleChange = (variance: VarianceType, checked: boolean) => {
    switch (variance) {
      case 'Cash Sale':
        // Only charge customers can adjust this setting
        if (model.activeCustomerID && model.customerParams.customerCashOnly) {
          toastr.error('Error', `Customer ${model.activeCustomerID} is cash only.`);
          return;
        } else {
          model.forceCashSale = checked;
        }
        break;
      case 'Sales Tax':
        const taxableCustomer = model.activeCustomerID && model.customerParams.taxable;
        const suppressSalesTaxPermission = model.activeCustomerID && model.customerParams.suppressSalesTax;

        // Request to add sales tax
        if (checked) {
          // Case 1: Reverting previously applied sales tax suppression for a taxable customer (if ACU1 allowed)
          if (taxableCustomer) {
            model.suppressSalesTax = false;
            // Case 2: Non-taxed customer requesting sales tax addition (forced sales tax)
          } else {
            model.forceSalesTax = true;
          }

          // Request to remove sales tax
        } else {
          // Case 1: Suppressing sales tax for a taxable customer (if ACU1 allows)
          if (taxableCustomer) {
            if (suppressSalesTaxPermission) {
              model.suppressSalesTax = true;
            } else {
              toastr.error(
                'Error',
                `Customer ${model.activeCustomerID} does not have permission to suppress sales tax.`
              );
              return;
            }
            // Case 2: Reverting previously applied forced sales tax on non-taxable customer
          } else {
            model.forceSalesTax = false;
          }
        }

        break;
      case 'Core Charges':
        // In this case, a model variance is set when the variance switch is OFF.
        model.suppressCoreCharges = !checked;
        break;
      case 'Fees':
        // This code will never be reached if !sysParams.feeSupression
        model.suppressFees = !checked;
        break;
      case 'No History Sale':
        model.noHistorySale = checked;
        break;
      default:
        break; // no-op
    }

    // Update the switch position
    setVariances({ [variance]: checked } as VariancesState);
  };

  return (
    <Modal backdrop="static" container={container as any} onHide={hide as any} show={show} size="sm">
      <Modal.Header closeButton={true}>
        <Modal.Title>Variances</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(Object.keys(variances) as VarianceType[]).map((variance, idx) => (
          <React.Fragment key={`var-${idx}`}>
            <div className="sk-varianceSwitch">
              <Switch
                checked={variances[variance]}
                onChange={(checked: boolean) => handleChange(variance, checked)}
                onColor="#9dd5a0"
                onHandleColor="#4caf50"
                height={30}
                width={60}
                handleDiameter={30}
                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
              />
              <div className="sk-varianceText">{variance}</div>
            </div>
            {variance === 'Sales Tax' &&
              model.suppressSalesTax &&
              model.sysParams.requireNonTaxReason &&
              model.sysParams.nonTaxReasonCodes.length > 0 && (
                <div className="sk-varianceNonTaxReason">
                  <Label>Non-Tax Reason</Label>
                  <select
                    defaultValue={model.nonTaxReasonCode!}
                    onChange={e => model.setNonTaxReasonCode(e.target.value)}
                  >
                    {model.sysParams.nonTaxReasonCodes.map((code, idx2) => (
                      <option value={code.code} key={`rc-${idx2}`}>
                        {code.reason}
                      </option>
                    ))}
                  </select>
                </div>
              )}
          </React.Fragment>
        ))}
        {model.activeCustomerID &&
          Array.isArray(model.customerParams.fleetCards) &&
          model.customerParams.fleetCards.length > 1 && (
            <div className="sk-varianceFleetCCSelection">
              <Label>Fleet Billing Account</Label>
              <select
                disabled={(model.activeCustomerID && model.customerParams.customerCashOnly) || model.forceCashSale}
                defaultValue={model.fleetBillingCCVariance ?? 1}
                onChange={e => (model.fleetBillingCCVariance = Number(e.target.value))}
              >
                {model.customerParams.fleetCards.map(card => (
                  <option key={`fleetcc-${card.id}`} value={card.id}>
                    {`${card.name} xxx-${card.last4} Exp ${card.expiration}`}
                  </option>
                ))}
              </select>
            </div>
          )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => hide()} variant="secondary">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VariancePanelModal;
